<script lang="ts">

  import { goto } from "$app/navigation"
  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import Loader from "$lib/components/loader.svelte"
  import { Button } from "$lib/components/ui/button"
  import { CalendarMonth } from "$lib/components/ui/calendar-month"
  import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "$lib/components/ui/command"
  import { FieldMessage } from "$lib/components/ui/field"
  import { ImageWithFallback } from "$lib/components/ui/image-with-fallback"
  import { Input } from "$lib/components/ui/input"
  import { Label } from "$lib/components/ui/label"
  import { Popover, PopoverButton, PopoverContent, PopoverTrigger } from "$lib/components/ui/popover"
  import type { _response } from "$lib/data/type/_response"
  import { createSearch } from "$lib/hooks/search.svelte"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date"
  import CalendarIcon from "@lucide/svelte/icons/calendar"
  import FilterIcon from "@lucide/svelte/icons/filter"
  import HistoryIcon from "@lucide/svelte/icons/history"
  import LayoutGridIcon from "@lucide/svelte/icons/layout-grid"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw"
  import TablePropertiesIcon from "@lucide/svelte/icons/table-properties"
  import XIcon from "@lucide/svelte/icons/x"
  import { createForm } from "@tanstack/svelte-form"
  import { createInfiniteQuery, createMutation, createQuery, useQueryClient } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { tick } from "svelte"
  import { z } from "zod/v4"
  import DialogFormItem from "./_components/dialog-form-item.svelte"
  import NavItem from "./_components/nav-item.svelte"
  import TableHistory from "./_components/table-history.svelte"
  import {
    OrderFormSchema,
    OrderItemEmpty,
    type Order,
    type OrderHistory,
    type OrderHistoryMode,
    type OrderHistoryTax,
    type OrderItem,
    type Product,
    type Relation,
  } from "../_types"

  const FormSchema = OrderFormSchema
  type FormData = z.infer<typeof FormSchema>
  const FormEmpty = FormSchema.parse({})

  const prefix = $derived(page.params.prefix ?? "0")
  const select = $derived(page.params.id ?? "create")
  const itemDialog = useDialog()


  let scrollEl = $state<HTMLElement>()
  let sentinel = $state<HTMLDivElement>()
  type Tab = "order" | "history" | "product"

  let tab = $state<Tab>("order")
  const scrollTops: Record<Tab, number> = { order: 0, history: 0, product: 0 }
  let filterGroup = $state<Relation>({ id: 0, name: "", order: 0 })
  let dateStart = $state<CalendarDate>(today(getLocalTimeZone()).set({ month: 1, day: 1 }))
  let dateEnd = $state<CalendarDate>(today(getLocalTimeZone()))
  let historyMode = $state<OrderHistoryMode>("all")
  let historyUnit = $state("")
  let historyTax = $state<OrderHistoryTax>("all")
  const historyModes: { id: OrderHistoryMode, name: string }[] = [
    { id: "all", name: "All" },
    { id: "yoy", name: "YoY" },
  ]
  const historyTaxes: { id: OrderHistoryTax, name: string }[] = [
    { id: "all", name: "All PPN" },
    { id: "exc", name: "Exc PPN" },
    { id: "inc", name: "Inc PPN" },
  ]
  let item = $state<OrderItem>(OrderItemEmpty)
  let itemIndex = $state(-1)
  let error = $state("")

  const queryClient = useQueryClient()

  const form = createForm(() => ({
    defaultValues: FormEmpty,
  }))
  const formValues = form.useSelector((state) => state.values)

  const groups = createQuery(() => ({
    queryKey: ["order-group", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/order/data/master-product-group`)
      return response.data.result!
    },
  }))
  const productSearch = createSearch()
  const products = createInfiniteQuery(() => ({
    queryKey: ["order-product", {
      prefix,
      customer_id: formValues.current.customer_id,
      group_id: filterGroup.id,
      search: productSearch.debounced
    }],
    enabled: formValues.current.customer_id > 0,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {

      const response = await httpClient.get<_response<Product[]>>(`/api/${prefix}/order/data/master-product`, {
        params: {
          customer_id: formValues.current.customer_id,
          group_id: filterGroup.id,
          page: pageParam,
          search: productSearch.debounced,
        },
      })

      return response.data.result!
    },
    getNextPageParam: (last, pages) => (last.length === 0 ? undefined : pages.length),
  }))
  const productData = $derived(products.data?.pages.flatMap((items) => items) ?? [])

  const histories = createQuery(() => ({
    queryKey: ["order-history", {
      prefix,
      customer_id: formValues.current.customer_id,
      date_start: dateStart.set({ day: 1 }).toString(),
      date_end: dateEnd.set({ day: 1 }).toString(),
      mode: historyMode,
      unit: historyUnit,
      tax: historyTax
    }],
    enabled: tab === "history" && formValues.current.customer_id > 0,
    queryFn: async () => {

      const response = await httpClient.get<_response<OrderHistory>>(`/api/${prefix}/order/data/history`, {
        params: {
          customer_id: formValues.current.customer_id,
          date_start: dateStart.set({ day: 1 }).toString(),
          date_end: dateEnd.set({ day: 1 }).toString(),
          mode: historyMode,
          unit: historyUnit,
          tax: historyTax,
        },
      })

      return response.data.result!
    },
  }))
  const historyData = $derived(histories.data)
  const historyUnits = $derived(["", ...(historyData?.units ?? [])])

  const query = createQuery(() => ({
    queryKey: ["order-detail-form", prefix, select],
    queryFn: async () => {

      const id = select == "" ? "create" : select
      const response = await httpClient.get<_response<Order>>(`/api/${prefix}/order/data/${id}`)
      const responseData = response.data.result!

      form.reset({
        ...FormEmpty,
        id: responseData.id,
        date: responseData.date,
        number: responseData.number,
        customer_id: responseData.customer.id as number,
        customer_name: responseData.customer.name,
        customer_address: responseData.customer.address ?? "",
        type_id: responseData.type?.id as string,
        type_name: responseData.type?.name ?? "",
        price_list_name: responseData.price_list_name,
        price_list_discount: responseData.price_list_discount,
        description: responseData.description ?? "",
        total_sub: responseData.total_sub,
        total_discount: responseData.total_discount,
        total: responseData.total,
        total_paid: responseData.total_paid,
        status_id: responseData.status.id,
        status_name: responseData.status.name,
        items: responseData.items.map((item) => ({
          ...item,
          product_id: item.product!.id,
          product_number: item.product!.number,
          product_name: item.product!.name,
        })),
      })

      return responseData
    },
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormData) => {

      const id = select == "create" ? "" : select
      const response = await httpClient.post<_response<null>>(`/api/${prefix}/order/data/${id}`, {
        _method: id == "" ? "POST" : "PATCH",
        ...data,
      })
      const responseData = response.data

      if (!data.action) return

      if (response.status === 202) {
        await queryClient.invalidateQueries({ queryKey: ["order-detail-form", prefix, select] })
      } else {
        form.reset(data)
      }
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (previous) => ({
          ...previous,
          errorMap: { onServer: { message: messages[0] } },
        }))
      })

      if (response.status !== 200) {
        form.setFieldValue("action", "")
        throw new Error(responseData.message)
      }

      if (data.action === "confirm") {
        toast.success(responseData.message)
        await goto(`/${prefix}/order`, { replaceState: true })
        return
      }

      form.setFieldValue("action", "confirm")
      toast.success(responseData.message)
      void queryClient.invalidateQueries({ queryKey: ["order-detail-form", prefix, select] })

      return
    },
    onError: (cause: Error) => {
      error = cause.message
      toast.error("Terjadi kesalahan", { description: cause.message })
    },
  }))

  function recalculate(items: OrderItem[]) {
    form.setFieldValue("items", items)
    form.setFieldValue(
      "total_sub",
      items.reduce((sum, current) => sum + current.price * current.quantity, 0),
    )
    form.setFieldValue(
      "total_discount",
      items.reduce((sum, current) => sum + (current.price * current.quantity - current.total), 0),
    )
    form.setFieldValue(
      "total",
      items.reduce((sum, current) => sum + current.total, 0),
    )
  }

  function handleSubmit() {
    const data = form.state.values
    const action = data.action === "confirm" ? "confirm" : "check"
    form.setFieldValue("action", action)
    error = ""
    mutation.mutate({ ...data, action })
  }

  function handleItem(value: OrderItem, index: number) {
    item = { ...value }
    itemIndex = index
    itemDialog.setOpen(true)
  }

  function handleProduct(value: Product) {

    const existingIndex = formValues.current.items.findIndex((current) => current.product_id === value.id)

    if (existingIndex >= 0) {
      item = { ...formValues.current.items[existingIndex] }
      itemIndex = existingIndex
    } else {
      item = {
        ...OrderItemEmpty,
        ref_id: value.ref_id,
        product_id: value.id,
        product_number: value.number,
        product_name: value.name,
        quantity: 1,
        discount_percentage_raw: value.discount_percentage,
        price_raw: value.price,
        discount_percentage: value.discount_percentage,
        price: value.price,
        total: value.price,
        description: value.description,
        product: { id: value.id, name: value.name, number: value.number, order: 0 },
      }
      itemIndex = -1
    }

    itemDialog.setOpen(true)
  }

  function saveItem(value: OrderItem) {

    const items = [...formValues.current.items]

    if (itemIndex < 0) items.push({ ...value, order: items.length + 1 })
    else items[itemIndex] = value

    recalculate(items)

    form.setFieldValue("action", "")
    mutation.mutate(form.state.values)
  }

  function removeItem(index: number) {

    recalculate(formValues.current.items.filter((_, position) => position !== index))

    form.setFieldValue("action", "")
    mutation.mutate(form.state.values)
  }

  async function switchTab(next: Tab) {
    if (next === tab) return

    scrollTops[tab] = scrollEl?.scrollTop ?? 0
    tab = next
    await tick()
    if (scrollEl) scrollEl.scrollTop = scrollTops[next]
  }

  $effect(() => {
    if (tab !== "product" || !sentinel || !products.hasNextPage || products.isFetching) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void products.fetchNextPage()
      },
      { threshold: 0.5 },
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  })

</script>

<Loader
  show={mutation.isPending}
  className="z-50"/>
<main
  bind:this={scrollEl}
  class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/order`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full"
          aria-label="Tutup order form">
          <XIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Order Form</h1>
      </div>
      <div></div>
    </div>
    {#if tab === "history" && !query.isFetching}
      <div class="flex items-center gap-2 overflow-x-auto px-3 py-2">
        <Button
          variant="outline"
          size="icon"
          class="group shrink-0"
          aria-label="Muat ulang riwayat"
          data-loading={histories.isFetching}
          disabled={histories.isFetching}
          onclick={() => void histories.refetch()}>
          <LoaderCircleIcon
            class="invisible absolute animate-spin group-data-[loading=true]:visible"
            aria-hidden="true"/>
          <RefreshCwIcon
            class="group-data-[loading=true]:invisible"
            aria-hidden="true"/>
        </Button>
        <div class="shrink-0">
          <Popover>
            <PopoverButton
              class="w-auto"
              value={historyModes.find((current) => current.id === historyMode)?.name}/>
            <PopoverContent align="start">
              <Command shouldFilter={false}>
                <CommandList class="min-h-20">
                  <CommandEmpty>Tidak ada data</CommandEmpty>
                  <CommandGroup>
                    {#each historyModes as mode (mode.id)}
                      <CommandItem
                        value={mode.name}
                        onclick={() => {
                          historyMode = mode.id
                          if (mode.id === "yoy") dateStart = dateEnd.subtract({ years: 1 })
                          document.keyEsc()
                        }}>
                        {mode.name}
                      </CommandItem>
                    {/each}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div class="shrink-0">
          <Popover>
            <PopoverTrigger>
              {#snippet child({ props })}
                <Button
                  {...props}
                  variant="outline"
                  class="justify-start whitespace-nowrap text-left font-normal"
                  aria-label="Pilih bulan awal">
                  <CalendarIcon aria-hidden="true"/>
                  <span class="tabular-nums">{dateStart.format("MMM yyyy")}</span>
                </Button>
              {/snippet}
            </PopoverTrigger>
            <PopoverContent
              align="start"
              class="w-72 p-0">
              <CalendarMonth
                selected={dateStart}
                onSelect={(value) => {
                  dateStart = value
                  if (dateStart.compare(dateEnd) > 0) dateEnd = value
                  document.keyEsc()
                }}/>
            </PopoverContent>
          </Popover>
        </div>
        <div class="shrink-0">
          <Popover>
            <PopoverTrigger>
              {#snippet child({ props })}
                <Button
                  {...props}
                  variant="outline"
                  class="justify-start whitespace-nowrap text-left font-normal"
                  aria-label="Pilih bulan akhir">
                  <CalendarIcon aria-hidden="true"/>
                  <span class="tabular-nums">{dateEnd.format("MMM yyyy")}</span>
                </Button>
              {/snippet}
            </PopoverTrigger>
            <PopoverContent
              align="start"
              class="w-72 p-0">
              <CalendarMonth
                selected={dateEnd}
                onSelect={(value) => {
                  dateEnd = value
                  if (dateEnd.compare(dateStart) < 0) dateStart = value
                  document.keyEsc()
                }}/>
            </PopoverContent>
          </Popover>
        </div>
        <div class="shrink-0">
          <Popover>
            <PopoverButton
              class="w-auto"
              value={historyUnit || "Semua Unit"}/>
            <PopoverContent align="start">
              <Command shouldFilter={false}>
                <CommandList class="min-h-20">
                  <CommandEmpty>Tidak ada data</CommandEmpty>
                  <CommandGroup>
                    {#each historyUnits as unit (unit)}
                      <CommandItem
                        value={unit || "Semua Unit"}
                        onclick={() => {
                          historyUnit = unit
                          document.keyEsc()
                        }}>
                        {unit || "Semua Unit"}
                      </CommandItem>
                    {/each}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div class="shrink-0">
          <Popover>
            <PopoverButton
              class="w-auto"
              value={historyTaxes.find((current) => current.id === historyTax)?.name}/>
            <PopoverContent align="start">
              <Command shouldFilter={false}>
                <CommandList class="min-h-20">
                  <CommandEmpty>Tidak ada data</CommandEmpty>
                  <CommandGroup>
                    {#each historyTaxes as tax (tax.id)}
                      <CommandItem
                        value={tax.name}
                        onclick={() => {
                          historyTax = tax.id
                          document.keyEsc()
                        }}>
                        {tax.name}
                      </CommandItem>
                    {/each}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    {/if}
    {#if tab === "product" && !query.isFetching}
      <div class="flex items-center gap-2 px-3 py-2">
        <Button
          variant="outline"
          size="icon"
          class="group shrink-0"
          aria-label="Muat ulang produk"
          data-loading={products.isFetching}
          disabled={products.isFetching}
          onclick={() => void products.refetch()}>
          <LoaderCircleIcon
            class="invisible absolute animate-spin group-data-[loading=true]:visible"
            aria-hidden="true"/>
          <RefreshCwIcon
            class="group-data-[loading=true]:invisible"
            aria-hidden="true"/>
        </Button>
        <Input
          type="text"
          class="min-w-0 flex-1"
          autocapitalize="off"
          placeholder="Cari..."
          value={productSearch.state}
          oninput={(event) => productSearch.set(event.currentTarget.value)}/>
        <Popover>
          <PopoverTrigger>
            {#snippet child({ props })}
              <Button
                {...props}
                variant="outline"
                size="icon"
                aria-label="Filter produk">
                <FilterIcon aria-hidden="true"/>
              </Button>
            {/snippet}
          </PopoverTrigger>
          <PopoverContent
            align="end"
            class="relative grid w-72 gap-4 p-4"
            onOpenAutoFocus={(event) => event.preventDefault()}>
            <div class="space-y-1">
              <Label>Grup Produk</Label>
              <Popover>
                <PopoverButton
                  class="w-full min-w-0"
                  value={filterGroup.name || "Semua Group"}/>
                <PopoverContent>
                  <Command>
                    <CommandList class="min-h-20">
                      <CommandEmpty>Tidak ada data</CommandEmpty>
                      <CommandGroup>
                        {#each groups.data ?? [] as group (group.id)}
                          <CommandItem
                            value={group.name}
                            onclick={() => {
                              filterGroup = group
                              document.keyEsc()
                            }}>
                            {group.name}
                          </CommandItem>
                        {/each}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    {/if}
  </nav>
  {#if query.isFetching}
    <div class="flex min-h-dvh items-center justify-center text-sm">
      <LoaderCircleIcon
        class="mr-2 size-4 animate-spin"
        aria-hidden="true"/>
      Memuat...
    </div>
  {:else}
    <div class={tab === "order" ? "block" : "hidden"}>
      <div class="space-y-3 px-3 text-xs">
        <div class="h-1"></div>
        <div class="flex space-x-3">
          <div class="flex-1">
            <Label>Tanggal</Label>
            <div class="text-desc text-sm font-mono">{formValues.current.date.toDateFormat("dd/MM/yyyy")}</div>
          </div>
          <div class="flex-1">
            <Label>Nomor</Label>
            <div class="text-desc text-sm font-mono">{formValues.current.number || "-"}</div>
          </div>
        </div>
        <div>
          <Label>Customer</Label>
          <div class="text-desc text-sm">{formValues.current.customer_name || "-"}</div>
        </div>
        <div>
          <Label>Alamat</Label>
          <div class="text-desc text-sm">{formValues.current.customer_address || "-"}</div>
        </div>
        <div class="flex">
          <div class="flex-1">
            <Label>Tipe</Label>
            <div class="text-desc text-sm">{formValues.current.type_name || "-"}</div>
          </div>
          <div class="flex-1">
            <Label>Price List</Label>
            <div class="text-desc text-sm">{formValues.current.price_list_name || "-"}</div>
          </div>
        </div>
        <div>
          <Label>Keterangan</Label>
          <div class="text-desc text-sm">{formValues.current.description || "-"}</div>
        </div>
        <div class="flex space-x-3">
          <div class="flex-1">
            <Label>Sub Total</Label>
            <div class="text-desc text-sm tabular-nums">{formValues.current.total_sub.toLocaleString("en-US")}</div>
          </div>
          <div class="flex-1">
            <Label>Diskon</Label>
            <div class="text-desc text-sm tabular-nums">
              {formValues.current.total_discount.toLocaleString("en-US")}
            </div>
          </div>
        </div>
        <div>
          <Label>Total</Label>
          <div class="text-desc text-sm tabular-nums">{formValues.current.total.toLocaleString("en-US")}</div>
        </div>
        {#if error}
          <div
            class="text-xs text-destructive"
            role="alert">
            {error}
          </div>
        {/if}
      </div>
      <div class="space-y-3">
        <div class="h-px"></div>
        <div>
          <h3 class="mx-3 mb-1 text-lg font-semibold leading-none">Item</h3>
          {#if formValues.current.items.isEmpty()}
            <div class="px-3 py-4 text-center text-xs">Tidak ada data</div>
          {:else}
            {#each formValues.current.items as current (`${current.product_id}-${current.id}`)}
              <div class="flex items-stretch">
                <Button
                  type="button"
                  variant="ghost"
                  class="block h-auto min-w-0 flex-1 justify-start rounded-none px-3 py-3 text-left whitespace-normal hover:bg-muted"
                  onclick={() => handleItem(current, formValues.current.items.indexOf(current))}>
                  <div class="text-sm break-words">{current.product_name} {current.ref_id}</div>
                  <div class="flex items-start gap-3 text-xs leading-none text-muted-foreground">
                    <div>{current.quantity.toLocaleString("en-US")} {current.unit}</div>
                    <div class="flex-1"></div>
                    {#if current.discount_percentage === 100}
                      <div>-</div>
                    {:else if current.price === 0}
                      <div>{current.price.toLocaleString("en-US")}</div>
                    {:else}
                      <div class="text-end">
                        <div>{current.price.toLocaleString("en-US")}</div>
                        {#if current.discount_percentage > 0}
                          <div>
                            <span class="font-semibold text-primary">{current.discount_percentage}%</span>
                            <span class="line-through">{current.price_raw.toLocaleString("en-US")}</span>
                          </div>
                        {/if}
                      </div>
                    {/if}
                    <div class="w-24 text-end tabular-nums">
                      {current.is_free ? "Free" : current.total.toLocaleString("en-US")}
                    </div>
                  </div>
                  <FieldMessage
                    name={`items.${current.ref_id}.quantity`}
                    form={form}
                    class="mt-1"/>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  class="h-auto rounded-none pe-3 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  aria-label={`Hapus ${current.product_name}`}
                  onclick={() => removeItem(formValues.current.items.indexOf(current))}>
                  <XIcon aria-hidden="true"/>
                </Button>
              </div>
            {/each}
          {/if}
        </div>
        <div class="h-36"></div>
      </div>
    </div>
    <div class={tab === "history" ? "block" : "hidden"}>
      {#if histories.isFetching && !historyData}
        <div class="flex min-h-48 items-center justify-center text-sm text-muted-foreground">
          <LoaderCircleIcon
            class="mr-2 size-4 animate-spin"
            aria-hidden="true"/>
          Memuat...
        </div>
      {:else if histories.isError}
        <div
          class="flex flex-col items-center gap-2 px-3 py-4 text-center text-xs"
          role="alert">
          <div>Riwayat gagal dimuat</div>
          <Button
            variant="secondary"
            onclick={() => void histories.refetch()}>
            Coba lagi
          </Button>
        </div>
      {:else if !historyData || historyData.groups.isEmpty()}
        <div class="flex min-h-48 items-center justify-center text-sm">Tidak ada data</div>
      {:else}
        <TableHistory value={historyData}/>
      {/if}
      <div class="h-36"></div>
    </div>
    <div class={tab === "product" ? "block" : "hidden"}>
      {#if products.isFetching && productData.isEmpty()}
        <div class="flex min-h-48 items-center justify-center text-sm text-muted-foreground">
          <LoaderCircleIcon
            class="mr-2 size-4 animate-spin"
            aria-hidden="true"/>
          Memuat...
        </div>
      {:else if products.isError}
        <div
          class="flex flex-col items-center gap-2 px-3 py-4 text-center text-xs"
          role="alert">
          <div>Produk gagal dimuat</div>
          <Button
            variant="secondary"
            onclick={() => void products.refetch()}>
            Coba lagi
          </Button>
        </div>
      {:else if productData.isEmpty()}
        <div class="flex min-h-48 items-center justify-center text-sm">Tidak ada data</div>
      {:else}
        <div class="min-h-full">
          <div class="h-3"></div>
          <div class="grid grid-cols-2 gap-3 px-3">
            {#each productData as product (product.id)}
              <button
                type="button"
                class="w-full rounded-md border text-start"
                onclick={() => handleProduct(product)}>
                <ImageWithFallback
                  class="h-40 rounded-b-none text-center"
                  src={product.image_url}
                  alt={product.name}/>
                <div class="p-2 text-xs">
                  <div class="font-bold">{product.name}</div>
                  {#if product.discount_percentage === 0 || product.price_raw === 0}
                    <div class="text-muted-foreground">{product.price.toLocaleString("en-US")}</div>
                  {:else if product.discount_percentage > 0}
                    <div class="text-muted-foreground">
                      <span class="font-semibold">{product.price.toLocaleString("en-US")}</span>
                      <span class="line-through">{product.price_raw.toLocaleString("en-US")}</span>
                    </div>
                  {/if}
                </div>
              </button>
            {/each}
          </div>
        </div>
      {/if}
      <div class="h-36"></div>
      <div
        bind:this={sentinel}
        class="h-1">
      </div>
    </div>
    <nav class="fixed inset-x-0 bottom-0 z-20 mx-auto h-auto w-full max-w-3xl bg-background">
      <div class="flex items-center gap-2 border-t bg-muted px-3 py-1">
        <div class="flex flex-1 items-center gap-2 leading-none">
          <div class="text-xs">Total</div>
          <div class="text-sm font-semibold">{formValues.current.total.toLocaleString("en-US")}</div>
        </div>
        <Button
          variant={formValues.current.action === "confirm" ? "default" : "outline"}
          size="sm"
          class="shadow-none"
          disabled={mutation.isPending}
          onclick={handleSubmit}>
          {formValues.current.action === "confirm" ? "Proses Order" : "Cek Harga"}
        </Button>
      </div>
      <div class="flex h-14 w-full max-w-3xl justify-evenly">
        <NavItem
          label="Pesanan"
          active={tab === "order"}
          onClick={() => void switchTab("order")}>
          <TablePropertiesIcon
            class="size-8"
            aria-hidden="true"/>
        </NavItem>
        <NavItem
          label="Produk"
          active={tab === "product"}
          onClick={() => void switchTab("product")}>
          <LayoutGridIcon
            class="size-8"
            aria-hidden="true"/>
        </NavItem>
        <NavItem
          label="Riwayat"
          active={tab === "history"}
          onClick={() => void switchTab("history")}>
          <HistoryIcon
            class="size-8"
            aria-hidden="true"/>
        </NavItem>
      </div>
    </nav>
  {/if}
</main>
<DialogFormItem
  open={itemDialog.state}
  setOpen={itemDialog.setOpen}
  value={item}
  save={saveItem}/>
